// ============================================================
// COMBAT STATS RESOLVER - Transforms a Character + talent/trait
// instances into engine-ready combat stats. Pure functions, no
// React dependency. Called once per attack, not per hit.
// ============================================================
import { readEffects } from './effectItems';
import type {
  Character,
  CharacterWeapon,
  CombatWeapon,
  AttackerCombatStats,
  DefenderCombatStats,
  DefenderSheet,
  CombatInstance,
  CustomModifier,
} from './types';
import type { DefenderWeapon } from '../../types';
import type { TalentEffect } from './talentEffects';
import { TALENT_CATALOG } from './talentCatalog';
import { TRAIT_CATALOG } from './traitCatalog';
import { getEffectiveTalents } from './effectiveTalents';
import { getSkillRating, computeSkillMirrorBonus } from './normalize';
import { findParryWeaponIndex, calcWeaponParryMod } from '../parryWeapon';
import { hasWS } from '../weaponSpecials';
import { resistancesFromEffects } from '../resistances';
import { presetCode, FEAR_PRESETS } from '../exposures';
import { upgradeAreaSpecials } from '../areaShapes';

// ────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────

/** One member of the effect union, picked by its `type`. */
type EffectOf<K extends TalentEffect['type']> = Extract<TalentEffect, { type: K }>;

/** The effects of one type, in list order, typed as that type. */
function effectsOf<K extends TalentEffect['type']>(effects: TalentEffect[], type: K): EffectOf<K>[] {
  return effects.filter((e): e is EffectOf<K> => e.type === type);
}

/** Sum the `value` of every item, in list order. */
function sumValues(items: { value: number }[]): number {
  return items.reduce((sum, m) => sum + m.value, 0);
}

/**
 * A catalog entry's effects for one instance: the parameter substituted
 * where the entry takes one, the entry's own objects otherwise. An id the
 * catalog does not know contributes nothing.
 */
function instanceEffects(
  def: { effects: TalentEffect[]; parameterized: boolean } | undefined,
  parameter: string | undefined,
): TalentEffect[] {
  if (!def) return [];
  if (!def.parameterized || parameter === undefined) return def.effects;
  return def.effects.map((effect) => applyParameter(effect, parameter));
}

/** Collect all effects from a character's talents (own + trait-granted), resolving parameters */
function collectTalentEffects(character: Character): TalentEffect[] {
  return getEffectiveTalents(character).flatMap((inst) =>
    instanceEffects(TALENT_CATALOG[inst.talentId], inst.parameter),
  );
}

/**
 * Every INNATE exposure a sheet carries (v8.355.0): Fear (X) as its
 * dread preset, what the catalog traits grant through the `exposure`
 * effect, and what the Exposures tab typed - deduped, in that order.
 */
function innateExposures(character: Character, effects: TalentEffect[], fearRating: number): string[] {
  const fear = fearRating > 0 ? [presetCode(FEAR_PRESETS[Math.min(4, fearRating) - 1])] : [];
  const granted = effectsOf(effects, 'exposure').map((e) => e.code);
  const typed = [...granted, ...(character.exposures ?? [])].filter(Boolean);
  return [...new Set([...fear, ...typed])];
}

/** Collect all effects from a character's traits, resolving parameters */
function collectTraitEffects(character: Character): TalentEffect[] {
  return (character.traits ?? []).flatMap((inst) => instanceEffects(TRAIT_CATALOG[inst.traitId], inst.parameter));
}

/**
 * Every effect a character carries, talents and traits together.
 *
 * Exported so consumers outside the resolver (initiative, for one) read
 * the same list its own entry points do rather than collecting half of
 * it. Order is talents, traits, then the character's own effects.
 */
export function collectCharacterEffects(character: Character): TalentEffect[] {
  return [
    ...collectTalentEffects(character),
    ...collectTraitEffects(character),
    // What is always on - and, on a derived sheet, the stances that are up (engine/roster/stancedSheet).
    // An effect with a condition is not resolved here: it is applied to the attack it holds for
    // (engine/character/situational), where the situation is known.
    ...readEffects(character.permanentEffects).filter((effect) => !effect.when),
  ];
}

/** The parameter as a number, or `fallback` when it does not parse. */
function parsedOr(parameter: string, fallback: number): number {
  const num = parseFloat(parameter);
  return isNaN(num) ? fallback : num;
}

/** The parameter IS the effect's value (Machine 4, Regeneration 2...). */
function valueFromParameter<E extends { value: number }>(effect: E, parameter: string): E {
  return { ...effect, value: parsedOr(parameter, 0) };
}

/** How a parameter lands on each effect type that takes one; the rest ignore it. */
const PARAMETER_RULES: { [K in TalentEffect['type']]?: (effect: EffectOf<K>, parameter: string) => EffectOf<K> } = {
  wound_bonus: (effect, parameter) => ({ ...effect, value: effect.value * parsedOr(parameter, 1) }),
  resistance: (effect, parameter) => ({ ...effect, against: parameter }),
  flat_ap_bonus: valueFromParameter,
  daemonic_protection: valueFromParameter,
  regeneration: valueFromParameter,
  fear_rating: valueFromParameter,
  unnatural_characteristic: (effect, parameter) => {
    // Parameter format: "characteristic:multiplier" e.g. "tgh:2"
    const parts = parameter.split(':');
    return {
      ...effect,
      characteristic: parts[0] || '',
      multiplier: parts.length > 1 ? parseInt(parts[1], 10) || 2 : 2,
    };
  },
};

/**
 * Apply a parameter value to an effect descriptor.
 * For numeric effects, the parameter is parsed as a number.
 * For string effects (resistance.against, unnatural_characteristic), used as-is.
 */
function applyParameter(effect: TalentEffect, parameter: string): TalentEffect {
  const rule = PARAMETER_RULES[effect.type] as ((e: TalentEffect, p: string) => TalentEffect) | undefined;
  return rule ? rule(effect, parameter) : effect;
}

/**
 * Get the bonus for a characteristic (value / 10, floored).
 *
 * Exported since v8.67.0: the formula had been copy-pasted into the
 * overview sheet, the characteristics editor and the initiative roll.
 * One home, so a house rule about rounding could never disagree with
 * itself across three screens.
 */
export function characteristicBonus(value: number): number {
  return Math.floor(value / 10);
}

/** Every talent id a character has (own or trait-granted), for repeated lookups. */
function talentIdsOf(character: Character): Set<string> {
  return new Set(getEffectiveTalents(character).map((t) => t.talentId));
}

/** Check if a character has a specific trait */
function hasTrait(character: Character, traitId: string): boolean {
  return character.traits.some((t) => t.traitId === traitId);
}

/** Get trait parameter value or undefined */
function getTraitParam(character: Character, traitId: string): string | undefined {
  const inst = character.traits.find((t) => t.traitId === traitId);
  return inst?.parameter;
}

/**
 * Strength bonus - uses the EFFECTIVE str multiplier so trait floors
 * (e.g. Astartes ×2) reach melee damage (v6.27.0). Attacker and defender
 * share it, so the two sides can never disagree about one character.
 */
function strengthBonusOf(character: Character, unnatural: Record<string, number>): number {
  return characteristicBonus(character.characteristics.str.value) * unnatural.str;
}

/**
 * Effective unnatural multipliers per characteristic: the stored
 * per-characteristic multiplier FLOORED by any matching
 * unnatural_characteristic trait/talent effect (e.g. Astartes str:2/tgh:2).
 * A manually-set higher multiplier is never downgraded (v6.27.0 - see ADR).
 */
export function getEffectiveUnnaturalMultipliers(character: Character): Record<string, number> {
  const result: Record<string, number> = {};
  for (const [id, char] of Object.entries(character.characteristics)) {
    result[id] = char.unnaturalMultiplier;
  }
  for (const effect of effectsOf(collectCharacterEffects(character), 'unnatural_characteristic')) {
    if (effect.characteristic in result) {
      result[effect.characteristic] = Math.max(result[effect.characteristic], effect.multiplier);
    }
  }
  return result;
}

/**
 * The character as the SHEET derives it, before any weapon says otherwise.
 *
 * Needed by everything that has to answer "what would this be without the
 * override?" - the Armory, to know whether a weapon carries one at all and
 * what its reset button should reset TO, and `applyLoadoutSlots`, to avoid
 * reading a stored value as a fresh edit of itself.
 *
 * Shared rather than written twice, because the two uses must agree: if the
 * editor's idea of "the character's value" and the writer's ever diverge,
 * opening a weapon and saving it unchanged silently invents an override.
 */
export function withoutWeaponOverrides(character: Character): Character {
  return {
    ...character,
    weapons: (character.weapons ?? []).map(
      ({ skillValue: _skill, strengthBonus: _str, canSwiftAttack: _swift, canLightningAttack: _lightning, ...rest }) =>
        rest,
    ),
  };
}

// ────────────────────────────────────────────────────────────
// Resolver: Attacker Stats
// ────────────────────────────────────────────────────────────

/** Talents the attack flow reads as plain flags, keyed by the stat they set. */
const ATTACKER_TALENT_FLAGS = {
  // Combat action talent flags
  hasBerserkCharge: 'berserk_charge',
  hasHammerBlow: 'hammer_blow',
  // Phase 6: Re-roll talent flags
  hasBlademaster: 'blademaster',
  hasSureStrike: 'sure_strike',
  hasHunterOfAliens: 'hunter_of_aliens',
  hasScourgeOfHeretics: 'scourge_of_heretics',
  // Phase 7: Furious Assault
  hasFuriousAssault: 'furious_assault',
  // Deathwatch Training: RF auto-confirms vs Xenos-flagged targets (v6.29.0)
  hasDeathwatchTraining: 'deathwatch_training',
} as const;

/** One flag per entry of `table`: does the character have that talent? */
function talentFlags<K extends string>(talents: Set<string>, table: Record<K, string>): Record<K, boolean> {
  const out = {} as Record<K, boolean>;
  for (const flag of Object.keys(table) as K[]) out[flag] = talents.has(table[flag]);
  return out;
}

/** A bonus that applies to melee, to ranged, or to both (`all`), totalled per side. */
function scopedTotals(effects: { value: number; scope: 'melee' | 'ranged' | 'all' }[]): {
  melee: number;
  ranged: number;
} {
  const on = (side: 'melee' | 'ranged') => sumValues(effects.filter((e) => e.scope === side || e.scope === 'all'));
  return { melee: on('melee'), ranged: on('ranged') };
}

/** Which attack modes any effect unlocks. */
function unlockedModes(effects: TalentEffect[]): { swift: boolean; lightning: boolean } {
  const modes = effectsOf(effects, 'attack_mode_unlock');
  return { swift: modes.some((e) => e.mode === 'swift'), lightning: modes.some((e) => e.mode === 'lightning') };
}

/** Which range penalties any effect removes. */
function rangePenaltiesRemoved(effects: TalentEffect[]): {
  removeLongRangePenalty: boolean;
  removeExtremeRangePenalty: boolean;
} {
  const mods = effectsOf(effects, 'range_modifier');
  return {
    removeLongRangePenalty: mods.some((e) => e.removeLongRangePenalty),
    removeExtremeRangePenalty: mods.some((e) => e.removeExtremeRangePenalty),
  };
}

/** Frenzy, and the skill bonus that only counts while frenzied. */
function frenzyOf(talents: Set<string>, effects: TalentEffect[]): { isFrenzied: boolean; frenziedSkillBonus: number } {
  const isFrenzied = talents.has('frenzy');
  const frenzied = effectsOf(effects, 'skill_bonus').filter((e) => e.condition === 'frenzied' && e.skill === 'ws');
  return { isFrenzied, frenziedSkillBonus: isFrenzied ? sumValues(frenzied) : 0 };
}

/**
 * The weapon-facing half of the attacker: the skill it rolls, the
 * strength behind it, the modes it may use.
 */
function attackerFigures(
  character: Character,
  weapon: CharacterWeapon | undefined,
  effects: TalentEffect[],
  customMods: CustomModifier[],
) {
  // Base skill from the governing characteristic, and its unnatural ×N
  // (trait effects act as a floor)
  const governing = weapon?.type === 'melee' ? 'ws' : 'bs';
  const unnatural = getEffectiveUnnaturalMultipliers(character);
  const baseSkill = character.characteristics[governing].value;
  const skillBonus = sumValues(effectsOf(effects, 'skill_bonus').filter((e) => e.skill === governing && !e.condition));
  const modes = unlockedModes(effects);
  return {
    // A WEAPON'S OWN VALUE WINS, and replaces the resolved figure rather
    // than adding to it: the Armory shows the fully-resolved default
    // beside the field, so what a GM types there is the answer, not a
    // base for talents to modify again.
    skillValue: weapon?.skillValue ?? baseSkill + skillBonus + sumValues(customMods),
    skillCharValue: baseSkill,
    skillUnnaturalMultiplier: unnatural[governing],
    strengthBonus: weapon?.strengthBonus ?? strengthBonusOf(character, unnatural),
    canSwiftAttack: weapon?.canSwiftAttack ?? modes.swift,
    canLightningAttack: weapon?.canLightningAttack ?? modes.lightning,
    // Effective Righteous Fury: per-weapon override wins, else the character's
    // global default, else true (legacy default for pre-field characters).
    righteousFury: weapon?.righteousFury ?? character.righteousFury ?? true,
    psyRating: character.psyRating,
  };
}

/**
 * Resolve attacker combat stats from a Character and optional custom modifiers.
 * Returns stats needed for attack execution (skill, modes, bonuses).
 */
export function resolveAttackerStats(
  character: Character,
  weaponId: string,
  customMods: CustomModifier[] = [],
): AttackerCombatStats {
  const weapon = character.weapons.find((w) => w.id === weaponId);
  const effects = collectCharacterEffects(character);
  const talents = talentIdsOf(character);
  const damage = scopedTotals(effectsOf(effects, 'damage_bonus'));
  const critical = scopedTotals(effectsOf(effects, 'critical_bonus'));
  const calledShot = scopedTotals(effectsOf(effects, 'called_shot_reduction'));
  return {
    ...attackerFigures(character, weapon, effects, customMods),
    damageBonusMelee: damage.melee,
    damageBonusRanged: damage.ranged,
    ...frenzyOf(talents, effects),
    criticalBonusMelee: critical.melee,
    criticalBonusRanged: critical.ranged,
    calledShotReductionMelee: calledShot.melee,
    calledShotReductionRanged: calledShot.ranged,
    ...rangePenaltiesRemoved(effects),
    ...talentFlags(talents, ATTACKER_TALENT_FLAGS),
  };
}

// ────────────────────────────────────────────────────────────
// Resolver: Combat Weapon
// ────────────────────────────────────────────────────────────

/**
 * Phase 7: Toxic Trait - inject Toxic(N) into natural melee weapons.
 * RAW: Toxic applies to a creature's natural attacks, gated by NaturalWeapon special.
 *
 * A single parseable string - the two-element form ['Toxic', '5'] read
 * back as rating 0 through getWSP, so the trait never penalised the
 * Toughness test (fixed v8.196.2). A weapon already Toxic keeps its own.
 */
function withToxicTrait(character: Character, weapon: CharacterWeapon, specialRules: string[]): string[] {
  const toxicParam = getTraitParam(character, 'toxic_trait');
  if (weapon.type !== 'melee' || toxicParam === undefined) return specialRules;
  if (!hasWS(specialRules, 'NaturalWeapon') || hasWS(specialRules, 'Toxic')) return specialRules;
  return [...specialRules, `Toxic(${parseInt(toxicParam, 10) || 0})`];
}

/**
 * The lists and the area block a weapon carries only when it has them.
 * Conditionally spread: an explicit `undefined` kills Firestore sync.
 */
function optionalWeaponFields(
  weapon: CharacterWeapon,
  area: ReturnType<typeof upgradeAreaSpecials>['area'],
): Partial<CombatWeapon> {
  return {
    ...(weapon.afflictions?.length ? { afflictions: weapon.afflictions } : {}),
    ...(weapon.exposures?.length ? { exposures: weapon.exposures } : {}),
    ...(weapon.hallucinogens?.length ? { hallucinogens: weapon.hallucinogens } : {}),
    // The area block rides only when it has a shape (v8.369.0), a legacy chip's included (v8.372.7).
    ...(area && area.shape !== 'none' ? { area: { ...area } } : {}),
  };
}

/** What the attacker's resolved stats contribute to the weapon, and the sheet's own overrides. */
function wieldedFigures(weapon: CharacterWeapon, stats: AttackerCombatStats) {
  return {
    skillValue: stats.skillValue,
    strengthBonus: stats.strengthBonus,
    righteousFury: stats.righteousFury,
    canSwiftAttack: stats.canSwiftAttack,
    canLightningAttack: stats.canLightningAttack,
    swiftOverride: weapon.canSwiftAttack ?? null,
    lightningOverride: weapon.canLightningAttack ?? null,
  };
}

/** The weapon's own profile, copied as the sheet has it. */
function weaponProfile(weapon: CharacterWeapon) {
  return {
    id: weapon.id,
    name: weapon.name,
    weaponName: weapon.name,
    weaponType: weapon.type,
    damageExpression: weapon.damageExpression,
    damageType: weapon.damageType,
    penetration: weapon.penetration,
    range: weapon.range,
    rateOfFire: weapon.rateOfFire,
  };
}

/**
 * Resolve a CombatWeapon from a Character and weapon ID.
 * Merges weapon-intrinsic data with character-derived stats.
 * The result is structurally compatible with the existing Slot interface.
 */
export function resolveCombatWeapon(character: Character, weaponId: string): CombatWeapon | null {
  const weapon = character.weapons.find((w) => w.id === weaponId);
  if (!weapon) return null;

  const stats = resolveAttackerStats(character, weaponId);
  // A legacy shape chip on the sheet reads as the block it stood for (v8.372.7);
  // the sheet itself keeps the chip until its next save strips it.
  const upgradedArea = upgradeAreaSpecials(weapon);

  return {
    ...weaponProfile(weapon),
    specialRules: withToxicTrait(character, weapon, upgradedArea.specialRules),
    ...optionalWeaponFields(weapon, upgradedArea.area),
    psyRating: weapon.psyRating || character.psyRating,
    // A Character is a template: its ammo entry is magazine CAPACITY (the
    // editor exposes Max only), so a weapon always enters combat FULL.
    // Without this, a weapon edited to Max 60 kept the factory's current
    // 28 and spawned at 28/60 (v6.38.3).
    ammo: { ...weapon.ammo, current: weapon.ammo.max },
    attackMod: weapon.attackMod ?? 0,
    coneLength: weapon.coneLength ?? 0,
    ...(weapon.reach ? { reach: weapon.reach } : {}),
    ...(weapon.noise ? { noise: weapon.noise } : {}),
    ...(weapon.rules?.length ? { rules: weapon.rules } : {}),
    ...(weapon.tags?.length ? { tags: weapon.tags } : {}),
    ...(weapon.recipe ? { recipe: weapon.recipe } : {}),
    isEmpty: false,
    ...wieldedFigures(weapon, stats),
  };
}

// ────────────────────────────────────────────────────────────
// Resolver: Defender Stats
// ────────────────────────────────────────────────────────────

/** The characteristic bonuses and unnatural multipliers a defender is measured by. */
function bodyFigures(character: Character, unnatural: Record<string, number>) {
  return {
    name: character.name,
    toughnessBonus: characteristicBonus(character.characteristics.tgh.value),
    // Same shape as the attacker-side strBonus: characteristic bonus ×
    // effective unnatural multiplier, so trait floors (Astartes ×2) count.
    strengthBonus: strengthBonusOf(character, unnatural),
    willpowerBonus: characteristicBonus(character.characteristics.wp.value) * (unnatural.wp ?? 1),
    unnaturalToughness: unnatural.tgh,
    unnaturalAgility: unnatural.agi,
    unnaturalWeaponSkill: unnatural.ws,
    // The multipliers the SB/WPB above already carry, so a plain-target
    // test can count degrees on the BASE bonus (v8.310.11).
    unnaturalStrength: unnatural.str ?? 1,
    unnaturalWillpower: unnatural.wp ?? 1,
  };
}

/** Trait-derived flags. */
function traitFlags(character: Character) {
  return {
    stuffOfNightmares: hasTrait(character, 'stuff_of_nightmares'),
    astartes: hasTrait(character, 'astartes'),
    fromBeyond: hasTrait(character, 'from_beyond'),
    blind: hasTrait(character, 'blind'),
    incorporeal: hasTrait(character, 'incorporeal'),
    undying: hasTrait(character, 'undying'),
  };
}

/** GM rulings and display settings copied off the sheet, present only when set. */
function passedThrough(character: Character) {
  return {
    // Passed through, not derived: this is a GM's ruling about a person,
    // and no talent or trait can grant it. Spread conditionally so a
    // character who is nobody's protagonist carries no key at all.
    ...(character.protagonist ? { protagonist: character.protagonist } : {}),
    // Visual only, and 0 means unset - the truthy guard also keeps a
    // literal undefined out of the Firestore document.
    ...(character.tokenDiameter ? { tokenDiameter: character.tokenDiameter } : {}),
    // How the display draws a vehicle; absent unless chosen (old sheets stay byte-identical).
    ...(character.vehicleKind ? { vehicleKind: character.vehicleKind } : {}),
    // The pinned body plate figure; absent means Automatic (plan body-map).
    ...(character.figure ? { figure: character.figure } : {}),
  };
}

/** The Machine trait's armour, 0 when absent or unparseable. */
function machineArmourOf(character: Character): number {
  const machineParam = getTraitParam(character, 'machine');
  return machineParam === undefined ? 0 : parseInt(machineParam, 10) || 0;
}

/** Armour and soak the sheet grants on top of worn armour. */
function protections(character: Character, effects: TalentEffect[]) {
  return {
    machineAP: machineArmourOf(character),
    naturalArmour: sumValues(effectsOf(effects, 'flat_ap_bonus')),
    daemonicProtection: sumValues(effectsOf(effects, 'daemonic_protection')),
    regeneration: sumValues(effectsOf(effects, 'regeneration')),
    resistances: resistancesFromEffects(effects),
  };
}

/** The sheet's Fear rating, raised (never lowered) by any fear_rating effect. */
function fearRatingOf(character: Character, effects: TalentEffect[]): number {
  return effectsOf(effects, 'fear_rating').reduce(
    (worst, effect) => (effect.value > worst ? effect.value : worst),
    character.fearRating,
  );
}

/** Extra reactions of one kind that effects grant. */
function grantedReactions(effects: TalentEffect[], defence: 'dodge' | 'parry'): number {
  return effectsOf(effects, 'defence_grant')
    .filter((e) => e.defence === defence)
    .reduce((sum, e) => sum + e.additional, 0);
}

/** Dodge - tolerant accessor handles legacy {trained,bonus} shapes (v6.30.0) */
function dodgeOf(character: Character, effects: TalentEffect[]): DefenderSheet['dodge'] {
  const rating = getSkillRating(character, 'dodge');
  return {
    // An object never dodges (2026-10-26): it is a thing, not a fighter.
    canDodge: rating.trained && character.bodyType !== 'object',
    agility: character.characteristics.agi.value + computeSkillMirrorBonus(rating),
    additionalDodges: grantedReactions(effects, 'dodge'),
  };
}

/**
 * The weapon a defender parries with and what it does to the parry.
 *
 * Has weapons but none valid for parry (all Unwieldy or ranged-only):
 * no parry. No weapons at all: the trained skill decides (manual targets).
 * Frenzy is not handled here - it blocks at combat time via the
 * target.frenzied toggle, not at spawn time.
 */
function parryWeaponOf(weapons: DefenderWeapon[], trained: boolean) {
  const idx = findParryWeaponIndex(weapons);
  if (idx < 0) return { canParry: weapons.length === 0 && trained, modifier: 0, hasPowerField: false };
  const pw = weapons[idx];
  return {
    canParry: trained,
    modifier: calcWeaponParryMod(pw.specialRules),
    hasPowerField: hasWS(pw.specialRules, 'PowerField'),
    parryWeaponName: pw.name,
    // Ethereal's gate reads these at defence time (v8.287.0).
    parryWeaponSpecials: [...pw.specialRules],
  };
}

/** The parry block, the weapons it chose between, and the skill before weapon mods. */
function parryOf(character: Character, effects: TalentEffect[], hasDeflectShot: boolean) {
  const rating = getSkillRating(character, 'parry');
  // (tolerate partial characters - weapons array may be absent)
  const defenderWeapons: DefenderWeapon[] = (character.weapons ?? []).map((w) => ({
    id: w.id,
    name: w.name,
    type: w.type,
    specialRules: [...w.specialRules],
  }));
  const parryBaseSkill = character.characteristics.ws.value + computeSkillMirrorBonus(rating);
  const chosen = parryWeaponOf(defenderWeapons, rating.trained);
  const parry: DefenderSheet['parry'] = {
    // Nor parries.
    canParry: chosen.canParry && character.bodyType !== 'object',
    weaponSkill: parryBaseSkill + chosen.modifier,
    hasPowerField: chosen.hasPowerField,
    additionalParries: grantedReactions(effects, 'parry'),
    // Only present when a parry weapon was actually selected. Emitting
    // the key with an explicit `undefined` makes Firestore reject the
    // whole target document ("Unsupported field value: undefined"),
    // which silently kills target sync.
    ...(chosen.parryWeaponName !== undefined ? { parryWeaponName: chosen.parryWeaponName } : {}),
    ...(chosen.parryWeaponSpecials !== undefined ? { parryWeaponSpecials: chosen.parryWeaponSpecials } : {}),
    canParryRanged: hasDeflectShot && chosen.canParry,
  };
  return { parry, defenderWeapons, parryBaseSkill };
}

/** Talent-derived defence flags; Phase 5 added the conditional ones. */
function defenceTalents(talents: Set<string>, unnaturalToughnessBonus: number) {
  const ironJaw = talents.has('iron_jaw');
  return {
    hardy: talents.has('hardy'),
    ironJaw,
    ironJawToughness: ironJaw ? unnaturalToughnessBonus * 10 : 0,
    counterAttackAvailable: talents.has('counter_attack'),
    isFrenzied: talents.has('frenzy'),
    hasBattleRage: talents.has('battle_rage'),
    hasDeflectShot: talents.has('deflect_shot'),
  };
}

/** Body type: from a trait (e.g. amorphous) first, else the character's own field. */
function bodyTypeOf(character: Character, effects: TalentEffect[], isHorde: boolean): DefenderSheet['bodyType'] {
  // An object stays an object whatever a trait says (2026-10-26): its sheet has no traits to speak of.
  if (character.bodyType === 'object') return 'object';
  return effectsOf(effects, 'body_type')[0]?.bodyType ?? (character.bodyType || (isHorde ? 'horde' : 'humanoid'));
}

/** The horde, size and bookkeeping figures the sheet carries unchanged. */
function sheetFigures(character: Character, isHorde: boolean, fearRating: number) {
  return {
    forceField: { ...(character.forceField ?? { active: false, protectionRating: 0, overloadThreshold: 0 }) },
    // Size from the trait parameter, else the character
    size: getTraitParam(character, 'size') || character.size,
    woundsMax: character.wounds?.max ?? 15,
    isHorde,
    hordeMaxMagnitude: character.horde?.maxMagnitude ?? 0,
    fearRating,
    psyRating: character.psyRating ?? 0,
    xenos: character.xenos ?? false,
  };
}

/**
 * Resolve the SHEET half of a defender - everything derived from the
 * Character's characteristics, talents and traits, with no combat state
 * mixed in (v7.0.0).
 *
 * This is the expensive half: it walks TALENT_CATALOG and TRAIT_CATALOG.
 * It is a pure function of the Character object alone, which is what lets
 * the roster projection memoize it on Character identity.
 *
 * `resolveDefenderStats` below overlays a CombatInstance onto this and is
 * the unchanged entry point for all pre-roster callers.
 *
 * @param isHordeOverride Legacy escape hatch: a CombatInstance's horde flag
 *   can be mutated after construction to diverge from its character, and
 *   bodyType derivation reads it. Only the legacy wrapper passes this - the
 *   roster keeps `isHorde` on the sheet, so its calls stay pure functions of
 *   `character` alone and remain safe to memoize on Character identity.
 */
export function resolveDefenderSheet(character: Character, isHordeOverride?: boolean): DefenderSheet {
  const effects = collectCharacterEffects(character);
  const talents = talentIdsOf(character);
  const isHorde = isHordeOverride ?? character.horde?.isHorde ?? false;
  // Unnatural multipliers: stored values floored by trait effects (v6.27.0)
  const unnatural = getEffectiveUnnaturalMultipliers(character);
  const fearRating = fearRatingOf(character, effects);
  const toughness = characteristicBonus(character.characteristics.tgh.value) * unnatural.tgh;
  return {
    ...bodyFigures(character, unnatural),
    trueGrit: talents.has('true_grit'),
    ...traitFlags(character),
    ...passedThrough(character),
    ...protections(character, effects),
    // Fear (X) is a DREAD exposure (v8.354.0): the Willpower test to
    // attack at all, on the four-rung ladder, through the same readers
    // every hand-set exposure goes through.
    exposures: innateExposures(character, effects, fearRating),
    armour: { ...(character.armour?.locations ?? {}) },
    dodge: dodgeOf(character, effects),
    ...parryOf(character, effects, talents.has('deflect_shot')),
    ...sheetFigures(character, isHorde, fearRating),
    ...defenceTalents(talents, toughness),
    bodyType: bodyTypeOf(character, effects, isHorde),
  };
}

/**
 * Resolve defender combat stats from a CombatInstance.
 * Produces a DefenderCombatStats object structurally compatible with Target.
 *
 * Since v7.0.0 this is a thin overlay over `resolveDefenderSheet`: the
 * sheet half above, plus the instance's live combat state. Behaviour is
 * unchanged - the pre-existing test suite pins that.
 */
export function resolveDefenderStats(instance: CombatInstance, customMods: CustomModifier[] = []): DefenderCombatStats {
  // Drop the sheet-only extras; the rest maps 1:1 onto DefenderCombatStats.
  const {
    woundsMax: _woundsMax,
    isHorde: _isHorde,
    hordeMaxMagnitude: _hordeMaxMagnitude,
    ...sheet
  } = resolveDefenderSheet(instance.character, instance.horde?.isHorde);

  return {
    ...sheet,
    // Custom modifiers apply to toughness bonus (state, not sheet)
    toughnessBonus: sheet.toughnessBonus + sumValues(customMods),
    armour: { ...instance.armour },
    forceField: { ...instance.forceField },
    horde: { ...instance.horde },
    // Cover is a per-round tactical choice, so the roster keeps it in
    // CombatState; the legacy instance has none, so it comes off the sheet.
    cover: { ...instance.character.cover },
    wounds: { ...instance.wounds },
    conditions: [...instance.conditions],
    criticalDamage: [...instance.criticalDamage],
    roundCountdown: instance.roundCountdown,
    defensiveStance: false, // Default; set via UI toggle on Target
    // The legacy path has no live state to read, so it offers the
    // choice rather than an answer: 'auto' resolves to front with no map.
    vehicleFacing: sheet.bodyType === 'vehicle' ? 'auto' : null,
  };
}
